var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import Icon from "../Icon";
import Loader from "../Loader";
import Markdown from "../Markdown";
import Text from "../Text";
import { Flex } from "../grid";
import { useToast } from "../../hooks";
import { cloneDeep, keyBy } from 'lodash';
import React, { useState, useImperativeHandle, useCallback, forwardRef, } from 'react';
import { getAcceptHeader, styled } from "../../utils";
var MEGABYTE_DIVIDER = 1000000;
var FileContainer = styled(Flex)(function (_a) {
    var colors = _a.theme.colors;
    return ({
        borderBottom: "1px solid ".concat(colors.primary),
    });
});
var StyledText = styled(Text)({
    overflowWrap: 'anywhere',
    margin: '0px',
});
var StyledLabel = styled.label(function (_a) {
    var _b = _a.theme, radii = _b.radii, colors = _b.colors, space = _b.space, color = _a.color;
    return ({
        position: 'relative',
        backgroundColor: color || colors.white,
        borderRadius: radii[0],
        border: "2px solid ".concat(colors.primary),
        borderStyle: 'dotted',
        padding: space.large,
        display: 'block',
    });
});
var StyledInput = styled.input({
    display: 'none',
});
var DragDropFile = function (_a, ref) {
    var uploadDescription = _a.uploadDescription, fileExtensions = _a.fileExtensions, onFileUpload = _a.onFileUpload, onChange = _a.onChange, id = _a.id;
    var _b = useState({}), fileStatus = _b[0], setFileStatus = _b[1];
    var acceptHeaders = fileExtensions
        .map(function (extension) {
        return getAcceptHeader(extension);
    })
        .filter(function (header) { return header !== 'application/octet-stream'; });
    var addToast = useToast().addToast;
    var handleDrag = function (e) {
        e.preventDefault();
        e.stopPropagation();
    };
    var clearState = useCallback(function () {
        setFileStatus({});
    }, [setFileStatus]);
    var checkIfFilesAreReady = useCallback(function () {
        var ready = true;
        for (var _i = 0, _a = Object.values(fileStatus); _i < _a.length; _i++) {
            var status_1 = _a[_i].status;
            if (status_1 !== 'DONE') {
                ready = false;
                break;
            }
        }
        return ready;
    }, [fileStatus]);
    useImperativeHandle(ref, function () {
        return {
            clearChildState: clearState,
            areAllFilesReady: checkIfFilesAreReady,
        };
    }, [clearState, checkIfFilesAreReady]);
    var handleDrop = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            var newFiles_1 = {};
            var newStatus_1 = {};
            Object.keys(keyBy(e.dataTransfer.files, 'name')).forEach(function (key, idx) {
                var _a, _b, _c;
                if (((_a = fileStatus[key]) === null || _a === void 0 ? void 0 : _a.status) !== 'PROCESSING' &&
                    fileExtensions.includes((_c = (_b = e.dataTransfer.files[idx].name.split('.')) === null || _b === void 0 ? void 0 : _b.pop()) === null || _c === void 0 ? void 0 : _c.toLowerCase())) {
                    newFiles_1[key] = {
                        file: e.dataTransfer.files[idx],
                        status: 'PROCESSING',
                    };
                    newStatus_1[key] = {
                        status: 'PROCESSING',
                        size: e.dataTransfer.files[idx].size,
                    };
                }
                else {
                    if (fileStatus[key]) {
                        addToast("Datei ".concat(e.dataTransfer.files[idx].name, " wird immer noch hochgeladen"), 'error');
                    }
                    else {
                        addToast('falsche Dateierweiterung', 'error');
                    }
                }
            });
            setFileStatus(__assign(__assign({}, fileStatus), newStatus_1));
            Object.keys(newFiles_1).forEach(function (key) {
                onFileUpload(newFiles_1[key].file).then(function (result) {
                    setFileStatus(function (prevStatus) {
                        var copyStatus = cloneDeep(prevStatus);
                        copyStatus[key].status = result === true ? 'DONE' : 'ERROR';
                        result === true && onChange(newFiles_1[key].file);
                        return __assign(__assign({}, prevStatus), copyStatus);
                    });
                });
            });
        }
    };
    var handleChange = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.target.files && e.target.files[0]) {
            var newFiles_2 = {};
            Object.keys(keyBy(e.target.files, 'name')).forEach(function (key, idx) {
                var _a;
                if (((_a = fileStatus[key]) === null || _a === void 0 ? void 0 : _a.status) !== 'PROCESSING') {
                    newFiles_2[key] = {
                        file: e.target.files[idx],
                        status: 'PROCESSING',
                    };
                    var tempStatus = {};
                    tempStatus[key] = {
                        status: 'PROCESSING',
                        size: e.target.files[idx].size,
                    };
                    setFileStatus(__assign(__assign({}, fileStatus), tempStatus));
                }
                else {
                    addToast("Datei ".concat(key, " wird immer noch hochgeladen"), 'error');
                }
            });
            Object.keys(newFiles_2).forEach(function (key) {
                onFileUpload(newFiles_2[key].file).then(function (result) {
                    setFileStatus(function (prevStatus) {
                        var copyStatus = cloneDeep(prevStatus);
                        copyStatus[key].status = result === true ? 'DONE' : 'ERROR';
                        result === true && onChange(newFiles_2[key].file);
                        return __assign(__assign({}, prevStatus), copyStatus);
                    });
                });
            });
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledInput, { type: "file", multiple: true, id: id || 'drag-drop-upload', accept: acceptHeaders.join(','), onChange: handleChange }),
        React.createElement(StyledLabel, { id: "label-file-upload-".concat(id), htmlFor: id || 'drag-drop-upload', onDragEnter: handleDrag, onDragLeave: handleDrag, onDragOver: handleDrag, onDrop: handleDrop },
            React.createElement(Flex, { alignItems: "center", flexDirection: "column" },
                Object.entries(fileStatus).map(function (_a) {
                    var _b, _c;
                    var fileName = _a[0], value = _a[1];
                    var sizeInMb = (value.size / MEGABYTE_DIVIDER).toFixed(2);
                    var fileExtension = (_c = (_b = fileName.split('.')) === null || _b === void 0 ? void 0 : _b.pop()) === null || _c === void 0 ? void 0 : _c.toUpperCase();
                    return (React.createElement(FileContainer, { key: "file-".concat(fileName), flexDirection: "row", width: "100%", justifyContent: "space-between", paddingBottom: "extraTiny", mt: "extraTiny" },
                        React.createElement(Flex, { flexDirection: "column" },
                            React.createElement(StyledText, { marginLeft: "extraTiny" }, fileName),
                            React.createElement(Text, { fontWeight: "normal", fontSize: "extraTiny", color: "gray6" }, "".concat(fileExtension, " | ").concat(sizeInMb, " MB"))),
                        {
                            DONE: (React.createElement(Icon.GreenCheck, { height: "19px", width: "19px", style: { minHeight: '19px', minWidth: '19px' } })),
                            ERROR: (React.createElement(Icon.CircleError, { height: "19px", width: "19px", style: { minHeight: '19px', minWidth: '19px' } })),
                            PROCESSING: (React.createElement(Loader, { tiny: true, p: "0px", m: "0px", width: "19px", height: "19px", minHeight: "19px", minWidth: "19px" })),
                        }[fileStatus[fileName].status]));
                }),
                React.createElement(Icon.File, null),
                React.createElement(Markdown, { textAlign: "center" }, uploadDescription)))));
};
export default forwardRef(DragDropFile);
//# sourceMappingURL=DragDropFile.js.map